import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminContacts = () => {
  const { isAuthenticated } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, responded, spam
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const contactsSnapshot = await getDocs(collection(db, 'contacts'));
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by timestamp (newest first)
        contactsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setContacts(contactsList);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  const handleMarkAsResponded = async (contactId) => {
    try {
      await updateDoc(doc(db, 'contacts', contactId), {
        status: 'responded',
        respondedAt: new Date().toISOString()
      });
      
      // Update local state
      setContacts(contacts.map(contact => 
        contact.id === contactId 
          ? { ...contact, status: 'responded', respondedAt: new Date().toISOString() }
          : contact
      ));

      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact({ ...selectedContact, status: 'responded', respondedAt: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const handleMarkAsSpam = async (contactId) => {
    try {
      await updateDoc(doc(db, 'contacts', contactId), {
        status: 'spam',
        spamAt: new Date().toISOString()
      });
      
      // Update local state
      setContacts(contacts.map(contact => 
        contact.id === contactId 
          ? { ...contact, status: 'spam', spamAt: new Date().toISOString() }
          : contact
      ));
      
      // If the currently selected contact was updated, keep selection in sync
      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact({ ...selectedContact, status: 'spam', spamAt: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Error marking contact as spam:', error);
    }
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact message?')) {
      try {
        await deleteDoc(doc(db, 'contacts', contactId));
        setContacts(contacts.filter(contact => contact.id !== contactId));
        if (selectedContact && selectedContact.id === contactId) {
          setSelectedContact(null);
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (filter === 'new') return contact.status === 'new' || !contact.status;
    if (filter === 'responded') return contact.status === 'responded';
    if (filter === 'spam') return contact.status === 'spam';
    return contact.status !== 'spam';
  });

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white">
        <AdminHeader />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4">Loading contacts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
      <AdminHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Contact Messages</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
              }`}
            >
              All ({contacts.length})
            </button>
            <button
              onClick={() => setFilter('new')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                filter === 'new' ? 'bg-blue-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
              }`}
            >
              New ({contacts.filter(c => c.status === 'new' || !c.status).length})
            </button>
            <button
              onClick={() => setFilter('responded')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                filter === 'responded' ? 'bg-blue-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
              }`}
            >
              Responded ({contacts.filter(c => c.status === 'responded').length})
            </button>
            <button
              onClick={() => setFilter('spam')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                filter === 'spam' ? 'bg-blue-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
              }`}
            >
              Spam ({contacts.filter(c => c.status === 'spam').length})
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact List */}
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg">
            <div className="p-6 border-b border-[#4d4d4d]">
              <h2 className="text-xl font-bold">Messages</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredContacts.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  No contact messages found.
                </div>
              ) : (
                filteredContacts.map(contact => (
                  <div
                    key={contact.id}
                    onClick={() => setSelectedContact(contact)}
                    className={`p-4 border-b border-[#4d4d4d] cursor-pointer hover:bg-[#2a2a2a] transition-colors ${
                      selectedContact && selectedContact.id === contact.id ? 'bg-[#2a2a2a]' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium">{contact.name}</h3>
                      <div className="flex items-center space-x-2">
                        {(!contact.status || contact.status === 'new') && (
                          <span className="bg-blue-600 text-xs px-2 py-1 rounded">New</span>
                        )}
                        {contact.status === 'responded' && (
                          <span className="bg-green-600 text-xs px-2 py-1 rounded">Responded</span>
                        )}
                        {contact.status === 'spam' && (
                          <span className="bg-red-600 text-xs px-2 py-1 rounded">Spam</span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mb-1">{contact.email}</p>
                    <p className="text-sm text-gray-400 mb-2">{formatDate(contact.timestamp)}</p>
                    {contact.subject && (
                      <p className="text-sm font-medium mb-1">Subject: {contact.subject}</p>
                    )}
                    <p className="text-sm text-gray-300 line-clamp-2">{contact.message}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg">
            {selectedContact ? (
              <>
                <div className="p-6 border-b border-[#4d4d4d]">
                  <div className="flex justify-between items-start">
                    <h2 className="text-xl font-bold">Message Details</h2>
                    <div className="flex space-x-2">
                      {(!selectedContact.status || selectedContact.status === 'new') && (
                        <>
                          <button
                            onClick={() => handleMarkAsResponded(selectedContact.id)}
                            className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm"
                          >
                            Mark as Responded
                          </button>
                          <button
                            onClick={() => handleMarkAsSpam(selectedContact.id)}
                            className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                          >
                            Mark as Spam
                          </button>
                        </>
                      )}
                      {selectedContact.status !== 'spam' && selectedContact.status === 'responded' && (
                        <button
                          onClick={() => handleMarkAsSpam(selectedContact.id)}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                        >
                          Mark as Spam
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(selectedContact.id)}
                        className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400">Name</label>
                      <p className="font-medium">{selectedContact.name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Email</label>
                      <p className="font-medium">
                        <a href={`mailto:${selectedContact.email}`} className="text-blue-500 hover:text-blue-400">
                          {selectedContact.email}
                        </a>
                      </p>
                    </div>
                    {selectedContact.subject && (
                      <div>
                        <label className="text-sm text-gray-400">Subject</label>
                        <p className="font-medium">{selectedContact.subject}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm text-gray-400">Message</label>
                      <div className="bg-[#2a2a2a] p-4 rounded mt-2">
                        <p className="whitespace-pre-wrap">{selectedContact.message}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Received</label>
                      <p className="font-medium">{formatDate(selectedContact.timestamp)}</p>
                    </div>
                    {selectedContact.status === 'responded' && selectedContact.respondedAt && (
                      <div>
                        <label className="text-sm text-gray-400">Responded</label>
                        <p className="font-medium">{formatDate(selectedContact.respondedAt)}</p>
                      </div>
                    )}
                    
                    {/* Location Information */}
                    {selectedContact.locationData && (
                      <div>
                        <label className="text-sm text-gray-400">Location & IP</label>
                        <div className="bg-[#2a2a2a] p-4 rounded mt-2">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-400">IP:</span> {selectedContact.locationData.ip}</div>
                            <div><span className="text-gray-400">Country:</span> {selectedContact.locationData.country}</div>
                            <div><span className="text-gray-400">Region:</span> {selectedContact.locationData.region}</div>
                            <div><span className="text-gray-400">City:</span> {selectedContact.locationData.city}</div>
                            <div><span className="text-gray-400">Timezone:</span> {selectedContact.locationData.timezone}</div>
                            <div><span className="text-gray-400">ISP:</span> {selectedContact.locationData.isp}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Device Information */}
                    {selectedContact.deviceInfo && (
                      <div>
                        <label className="text-sm text-gray-400">Device & Browser</label>
                        <div className="bg-[#2a2a2a] p-4 rounded mt-2">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-400">Device:</span> {selectedContact.deviceInfo.deviceType}</div>
                            <div><span className="text-gray-400">OS:</span> {selectedContact.deviceInfo.os}</div>
                            <div><span className="text-gray-400">Browser:</span> {selectedContact.deviceInfo.browser}</div>
                            <div><span className="text-gray-400">Resolution:</span> {selectedContact.deviceInfo.screenResolution}</div>
                            <div><span className="text-gray-400">Language:</span> {selectedContact.deviceInfo.language}</div>
                            <div><span className="text-gray-400">Platform:</span> {selectedContact.deviceInfo.platform}</div>
                            {selectedContact.deviceInfo.isVirtualized && (
                              <div className="col-span-2">
                                <span className="bg-orange-600 text-xs px-2 py-1 rounded">Virtualized Browser Detected</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-6 text-center text-gray-400">
                Select a message to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts; 